// frontend/src/pages/superadmin/AgentsManager.jsx
import React, { useState, useEffect } from 'react';
import { Cpu, Search, Trash2, Eye, RefreshCw, XCircle, CheckCircle, Plus } from 'lucide-react';
import { getAirports, getAirportAgents, removeAirportAgent, getAirportLicense } from '../../services/superAdminApi';

const AgentsManager = () => {
  const [airports, setAirports] = useState([]);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [airportLicense, setAirportLicense] = useState(null);
  const [newAgent, setNewAgent] = useState({
    name: '',
    hardware_id: '',
    hostname: ''
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadAirports();
  }, []);

  const loadAirports = async () => {
    try {
      const response = await getAirports(1, 100, searchTerm);
      setAirports(response.data.airports || []);
    } catch (error) {
      console.error('Error loading airports:', error);
    }
  };

  const loadAgents = async (airportId) => {
    setLoading(true);
    try {
      const response = await getAirportAgents(airportId);
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error loading agents:', error);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAirportLicense = async (airportId) => {
    try {
      const response = await getAirportLicense(airportId);
      setAirportLicense(response.data);
    } catch (error) {
      console.error('Error loading license:', error);
      setAirportLicense(null);
    }
  };

  const handleSelectAirport = async (airport) => {
    setSelectedAirport(airport);
    await loadAirportLicense(airport.id);
    await loadAgents(airport.id);
  };

  const handleRemoveAgent = async (agentId) => {
    if (window.confirm('Remove this agent? It will no longer be able to send detections.')) {
      try {
        await removeAirportAgent(selectedAirport.id, agentId);
        loadAgents(selectedAirport.id);
        loadAirports(); // Refresh airport list to update count
      } catch (error) {
        console.error('Error removing agent:', error);
        alert('Error removing agent');
      }
    }
  };

  const handleCreateAgent = async () => {
    if (!newAgent.name) {
      alert('Please enter an agent name');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch('http://localhost:8001/video/agents/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          license_key: airportLicense?.license_key,
          api_key: `agent_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`,
          hardware_id: newAgent.hardware_id || `HW_${Date.now()}`,
          hostname: newAgent.hostname || `HOST_${Date.now()}`,
          version: "1.0.0",
          name: newAgent.name
        })
      });
      
      const data = await response.json();
      if (response.ok) {
        alert(`✅ Agent created successfully!\nAgent ID: ${data.agent_id}`);
        setShowCreateModal(false);
        setNewAgent({ name: '', hardware_id: '', hostname: '' });
        await loadAgents(selectedAirport.id);
        await loadAirports();
      } else {
        alert(`❌ Error: ${data.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating agent:', error);
      alert('Error creating agent');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">Agents Management</h1>
          <p className="text-gray-400 text-sm">Manage video surveillance agents per airport</p>
        </div>
        {selectedAirport && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Agent
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Airport List */}
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h2 className="text-white font-semibold mb-4">Airports</h2>
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg py-2 pl-10 pr-4 text-white text-sm"
            />
          </div>
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {airports.map((airport) => (
              <button
                key={airport.id}
                onClick={() => handleSelectAirport(airport)}
                className={`w-full text-left p-3 rounded-lg transition-all ${
                  selectedAirport?.id === airport.id
                    ? 'bg-blue-600/20 border-l-2 border-blue-500'
                    : 'hover:bg-gray-700/50'
                }`}
              >
                <p className="text-white font-medium">{airport.name}</p>
                <p className="text-gray-400 text-xs">{airport.subdomain}.aerotrick.com</p>
                <p className="text-gray-500 text-xs mt-1">
                  Agents: {airport.used_agents || 0}/{airport.max_agents || 5}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Agents List */}
        <div className="md:col-span-2 bg-gray-800/50 rounded-xl border border-gray-700 p-4">
          <h2 className="text-white font-semibold mb-4">
            {selectedAirport ? `Agents - ${selectedAirport.name}` : 'Select an airport'}
          </h2>
          
          {!selectedAirport ? (
            <p className="text-gray-400 text-center py-10">Select an airport from the left</p>
          ) : loading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : agents.length === 0 ? (
            <p className="text-gray-400 text-center py-10">No agents connected yet. Click "Add Agent" to create one.</p>
          ) : (
            <div className="space-y-3">
              {agents.map((agent) => (
                <div key={agent.id} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Cpu className="w-4 h-4 text-blue-400" />
                        <span className="text-white font-mono text-sm">{agent.agent_id}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          agent.status === 'active' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {agent.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <p><span className="text-gray-500">Name:</span> <span className="text-gray-300">{agent.name || '-'}</span></p>
                        <p><span className="text-gray-500">Hardware:</span> <span className="text-gray-300">{agent.hardware_id}</span></p>
                        <p><span className="text-gray-500">Hostname:</span> <span className="text-gray-300">{agent.hostname}</span></p>
                        <p><span className="text-gray-500">Version:</span> <span className="text-gray-300">{agent.version}</span></p>
                        <p><span className="text-gray-500">Registered:</span> <span className="text-gray-300">{formatDate(agent.registered_at)}</span></p>
                        <p><span className="text-gray-500">Last seen:</span> <span className="text-gray-300">{formatDate(agent.last_seen)}</span></p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveAgent(agent.agent_id)}
                      className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                      title="Remove agent"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-white font-semibold">Add New Agent</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Agent Name *</label>
                <input
                  type="text"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Camera Nord"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Hardware ID (optional)</label>
                <input
                  type="text"
                  value={newAgent.hardware_id}
                  onChange={(e) => setNewAgent({ ...newAgent, hardware_id: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Auto-generated if empty"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Hostname (optional)</label>
                <input
                  type="text"
                  value={newAgent.hostname}
                  onChange={(e) => setNewAgent({ ...newAgent, hostname: e.target.value })}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  placeholder="Auto-generated if empty"
                />
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-gray-700">
              <button
                onClick={handleCreateAgent}
                disabled={creating}
                className="flex-1 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white font-medium disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Agent'}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentsManager;